import React, { useState, useEffect, useRef } from "react";
import { useAlertPreferences, AlertPref } from "../hooks/useAlertPreferences";
import { getCounties } from "../api";
import { County } from "../types";
import CountyMultiSelect from "./CountyMultiSelect";

// ─── Zip code parsing + validation ──────────────────────────────────
//
// Users reliably type the wrong separator: `28213. 28215` or
// `28213;28215` or `28213 28215`. Rather than reject any of these,
// `parseZipsLoose` splits on [,.;\s]+ and keeps only valid 5-digit
// tokens, dedupes. `detectZipIssues` returns a human-readable list of
// problems so the UI can show a warning while the user types.
//
// On blur / Enter, the caller should rewrite the draft to the cleaned
// "28213, 28215" form so the auto-fix is visible.

function parseZipsLoose(raw: string): string[] {
  const tokens = raw.split(/[,.;\s]+/).map((t) => t.trim()).filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tokens) {
    // Drop obvious non-zip tokens (letters) but keep the string as-is
    // if it's a 5-digit numeric string.
    if (!/^\d{5}$/.test(t)) continue;
    if (seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function detectZipIssues(raw: string): string[] {
  const issues: string[] = [];
  if (!raw.trim()) return issues;
  // Bad separators: anything non-comma non-digit non-space
  if (/[.;]/.test(raw)) issues.push("use commas to separate zips");
  // Non-5-digit numeric tokens
  const rawTokens = raw.split(/[,.;\s]+/).map((t) => t.trim()).filter(Boolean);
  for (const t of rawTokens) {
    if (!/^\d{5}$/.test(t)) {
      issues.push(`"${t}" is not a 5-digit zip`);
      break; // one message is enough
    }
  }
  // Duplicates
  const seen = new Set<string>();
  for (const t of rawTokens) {
    if (/^\d{5}$/.test(t)) {
      if (seen.has(t)) {
        issues.push("duplicate zip removed");
        break;
      }
      seen.add(t);
    }
  }
  return issues;
}

const LABELS: Record<string, { title: string; desc: string }> = {
  new_upset_bid: { title: "New Upset Bid", desc: "When someone outbids the current offer on a watched property" },
  auction_reminder_7d: { title: "Auction 7-Day Reminder", desc: "One week before a watched property's auction" },
  auction_reminder_1d: { title: "Auction 1-Day Reminder", desc: "One day before a watched property's auction" },
  new_listing: {
    title: "New Listings in Area",
    desc: "Any new foreclosure in your zip codes or a whole county — covers all pipeline stages. Not sending yet: watchlist alerts are live, area alerts are coming.",
  },
  nod_new_filing: {
    title: "New Pre-Foreclosure Filings",
    desc: "Earliest pipeline stage — Notice of Default filings in your zip codes or a whole county, with maximum lead time before auction",
  },
  status_change: { title: "Status Changes", desc: "When a watched property's status updates" },
  new_notice_lead: {
    title: "New Notice Leads (Early)",
    desc: "Newly published foreclosure notices before we've matched a property record — county, sale date, and case number, before a confirmed address is available",
  },
};

interface AlertSettingsProps {
  isPro: boolean;
}

const AlertSettings: React.FC<AlertSettingsProps> = ({ isPro }) => {
  const { prefs, save, loading, capError, loadError, saveError, reload } = useAlertPreferences();

  // Active counties for the "whole county" dropdown. The backend already
  // matches new_listing / nod_new_filing on alert_preferences.watch_county
  // (see engine_listings.go) — this just exposes the control so a user can
  // subscribe to every property in a county instead of listing each zip.
  const [counties, setCounties] = useState<County[]>([]);
  useEffect(() => {
    getCounties()
      .then(setCounties)
      .catch(() => setCounties([]));
  }, []);

  // Local draft of the zip-code text input per alert type. Keyed by
  // alertType because the input is controlled — we can't rely on prefs
  // alone since that only has the saved (committed) value, not the
  // in-progress typing.
  const [zipDrafts, setZipDrafts] = useState<Record<string, string>>({});

  // Refs so the unmount cleanup sees the latest values without being
  // part of the effect's dependency list (which would re-run cleanup).
  const zipDraftsRef = useRef(zipDrafts);
  zipDraftsRef.current = zipDrafts;
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const saveRef = useRef(save);
  saveRef.current = save;
  // Tracks whether the first real server-load has been seeded into drafts.
  // Without this, the hook initialises prefs to ALL_ALERT_TYPES (with
  // watchZipCodes:[]) before the GET /alerts/preferences response arrives.
  // The seeding effect fires immediately on those default prefs, writing
  // zipDrafts["new_listing"] = "" — then when the real prefs land the
  // anti-clobber guard sees "" (not undefined) and silently discards the
  // user's saved zip codes (#504).
  const seeded = useRef(false);

  // On unmount (e.g. user closed the Settings panel without leaving the
  // zip input first), flush any unsaved drafts that differ from the
  // saved prefs. Without this, typing zips and then clicking Close races
  // with the blur handler — sometimes blur fires, sometimes the DOM is
  // removed first and the keystrokes are lost.
  useEffect(() => {
    return () => {
      const drafts = zipDraftsRef.current;
      const current = prefsRef.current;
      const next = current.map((p) => {
        const draft = drafts[p.alertType];
        if (draft === undefined) return p;
        const parsed = parseZipsLoose(draft);
        const saved = (p.watchZipCodes || []).join(",");
        if (parsed.join(",") === saved) return p;
        return { ...p, watchZipCodes: parsed };
      });
      const changed = next.some(
        (p, i) =>
          (p.watchZipCodes || []).join(",") !==
          (current[i].watchZipCodes || []).join(",")
      );
      if (changed) {
        saveRef.current(next);
      }
    };
  }, []);

  // Seed zip drafts from prefs. The first seed after the server load
  // completes is unconditional so saved zip codes always display on reload
  // (#504). Subsequent updates (triggered by in-session saves) respect the
  // anti-clobber guard so mid-edit typing is never overwritten.
  useEffect(() => {
    if (loading) return;
    if (!seeded.current) {
      seeded.current = true;
      setZipDrafts(() => {
        const next: Record<string, string> = {};
        for (const p of prefs) {
          next[p.alertType] = (p.watchZipCodes || []).join(", ");
        }
        return next;
      });
      return;
    }
    // Post-load updates (saves): only initialise drafts for newly added
    // alert types; don't clobber what the user is actively typing.
    setZipDrafts((prev) => {
      const next = { ...prev };
      for (const p of prefs) {
        if (next[p.alertType] === undefined) {
          next[p.alertType] = (p.watchZipCodes || []).join(", ");
        }
      }
      return next;
    });
  }, [loading, prefs]);

  const togglePref = (alertType: string, enabled: boolean) => {
    // Preserve any in-progress zip draft when toggling — parse the draft
    // and flush it along with the toggle so enabling an alert never loses
    // zip codes the user already typed. Uses parseZipsLoose so a draft
    // like "28213. 28215" auto-corrects to ["28213", "28215"].
    const draftZips = parseZipsLoose(zipDrafts[alertType] ?? "");
    const next = prefs.map((p) =>
      p.alertType === alertType
        ? { ...p, enabled, watchZipCodes: draftZips.length > 0 ? draftZips : p.watchZipCodes }
        : p
    );
    save(next);
  };

  // Toggle a county in/out of the watchCounties array (#505 multi-county support).
  // watchCounties and watchZipCodes are OR'd by the backend engine.
  const commitCounties = (alertType: string, countyId: string, checked: boolean) => {
    const next = prefs.map((p) => {
      if (p.alertType !== alertType) return p;
      const current = p.watchCounties ?? [];
      const updated = checked
        ? [...current.filter((c) => c !== countyId), countyId]
        : current.filter((c) => c !== countyId);
      return { ...p, watchCounties: updated };
    });
    save(next);
  };

  const commitZips = (alertType: string) => {
    // parseZipsLoose strips dots, semicolons, spaces, non-5-digit tokens,
    // and duplicates. Then we rewrite the draft to the cleaned form so
    // the user visibly sees the auto-fix happen ("28213. 28215" becomes
    // "28213, 28215" in the input after blur).
    const zips = parseZipsLoose(zipDrafts[alertType] ?? "");
    setZipDrafts((prev) => ({ ...prev, [alertType]: zips.join(", ") }));
    const next = prefs.map((p) =>
      p.alertType === alertType ? { ...p, watchZipCodes: zips } : p
    );
    save(next);
  };

  if (loading) return <div>Loading…</div>;

  // Never render the default toggles after a failed load: they would look like
  // the user's saved settings and the next change would overwrite the real ones.
  if (loadError) {
    return (
      <div className="alert-settings">
        <div className="alert-cap-banner" role="alert">
          <span>We couldn't load your alert settings.</span>
          <button type="button" className="alert-cap-upgrade" onClick={reload}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="alert-settings">
      <h3>Email Alerts</h3>
      {/* #782: free tier gets a combined cap of 5 emails/rolling-30-days on
          New Listings + New Pre-Foreclosure Filings (#781), extended to
          include New Notice Leads by #933 (see the ASSUMPTION on
          listingAlertQuotaExceeded in engine_listings.go — flagged there for
          the founder to correct if a separate cap was actually intended).
          Enforcement is silent and server-side (engine_listings.go
          suppresses sends past the cap at generation time, not save time) —
          there's no API call the frontend can react to, so without this
          notice a free user who stops getting these alerts would have no
          way to tell a real plan limit from a broken feature. Static copy
          only (no live remaining-count endpoint exists yet); reuses the
          same informational-banner treatment as the #505 whole-county cap
          notice below. Hidden for any paid tier — scout is exempt from this
          quota same as pro/team, matching alertQuotaApplies() in the
          backend. */}
      {!isPro && (
        <div className="alert-cap-banner">
          <span>
            Free plan: New Listings, New Pre-Foreclosure Filings, and New
            Notice Leads alerts are limited to 5 emails combined per rolling
            30 days. Upgrade to Pro for unlimited alerts.
          </span>
          <a href="/pricing" className="alert-cap-upgrade">
            See plans →
          </a>
        </div>
      )}
      {/* #505: whole-county alert cap hit — the over-cap selection was reverted
          by the hook; nudge toward the plan that lifts the cap. */}
      {capError && (
        <div className="alert-cap-banner" role="alert">
          <span>{capError.message}</span>
          {capError.upgradeUrl && (
            <a href={capError.upgradeUrl} className="alert-cap-upgrade">
              See plans →
            </a>
          )}
        </div>
      )}
      {saveError && (
        <div className="alert-cap-banner" role="alert">
          <span>{saveError}</span>
        </div>
      )}
      {/* nod_new_filing is hidden: NC has no notice-of-default instrument, no
          writer ever sets that status, so the alert can never fire. The pref
          stays in the payload so existing rows are untouched. */}
      {prefs.filter((p) => p.alertType !== "nod_new_filing").map((p: AlertPref) => {
        const meta = LABELS[p.alertType] || { title: p.alertType, desc: "" };
        return (
          <div key={p.alertType} className="alert-row">
            <div className="alert-info">
              <div className="alert-title">{meta.title}</div>
              <div className="alert-desc">{meta.desc}</div>
              {(p.alertType === "new_listing" || p.alertType === "nod_new_filing" || p.alertType === "new_notice_lead") && p.enabled && (
                <>
                  <input
                    type="text"
                    className="alert-zips"
                    aria-label={`${meta.title} zip code filter`}
                    placeholder="Zip codes, comma-separated (e.g. 28213, 28215)"
                    value={zipDrafts[p.alertType] ?? ""}
                    onChange={(e) =>
                      setZipDrafts((prev) => ({ ...prev, [p.alertType]: e.target.value }))
                    }
                    onBlur={() => commitZips(p.alertType)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                  />
                  {(() => {
                    const issues = detectZipIssues(zipDrafts[p.alertType] ?? "");
                    if (issues.length === 0) return null;
                    return (
                      <div className="alert-zips-warning" role="status">
                        ⚠ {issues.join(" · ")} — we'll auto-fix on save
                      </div>
                    );
                  })()}
                  {counties.length > 0 && (
                    <div className="alert-county-row">
                      <span className="alert-county-label">Or whole counties:</span>
                      {/* Searchable multi-select instead of a flat 100-county
                          checklist. Same per-county onChange contract, so the
                          #505 cap (422 revert + capError banner) is unchanged. */}
                      <CountyMultiSelect
                        counties={counties}
                        selected={p.watchCounties ?? []}
                        onToggle={(countyId, checked) => commitCounties(p.alertType, countyId, checked)}
                        label={`${meta.title} counties`}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <label className="alert-switch">
              <input
                type="checkbox"
                aria-label={meta.title}
                checked={p.enabled}
                onChange={(e) => togglePref(p.alertType, e.target.checked)}
              />
              <span className="alert-slider" />
            </label>
          </div>
        );
      })}
    </div>
  );
};

export default AlertSettings;
